import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Reuses the same REACT_APP_API_URL every other component in this app
// already calls (e.g. `${REACT_APP_API_URL}AllPackages`) - no second API
// base is introduced. .env sets it with a trailing slash, so this resolves
// to https://api.travellerchoiceladakh.com/api/seo?path=...
const API_BASE = process.env.REACT_APP_API_URL;

// In-memory, per-session cache keyed by pathname so navigating back to a
// route already visited this session never re-fetches, and two components
// requesting the same path at once share one request.
const cache = new Map();
const inFlight = new Map();

function normalizeRecord(json) {
  if (!json || json.status !== true || !json.data) return null;
  const data = json.data;
  if (typeof data !== "object" || Array.isArray(data)) return null;
  return data;
}

function fetchSeoForPath(pathname) {
  if (cache.has(pathname)) return Promise.resolve(cache.get(pathname));
  if (inFlight.has(pathname)) return inFlight.get(pathname);

  const url = `${API_BASE}seo?path=${encodeURIComponent(pathname)}`;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.log("[useSeoMeta] requesting", url);
  }

  const request = fetch(url)
    .then((res) => (res.ok ? res.json() : null))
    .then((json) => normalizeRecord(json))
    .catch(() => null) // network error / bad JSON -> treat as "no record"
    .then((result) => {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.log("[useSeoMeta] resolved", pathname, result);
      }
      cache.set(pathname, result);
      inFlight.delete(pathname);
      return result;
    });

  inFlight.set(pathname, request);
  return request;
}

/**
 * Fetches centralized route SEO from the Laravel `GET {API_BASE}seo?path=`
 * endpoint for the current route (or an explicit `path` override).
 *
 * Returns `null` while loading, on a 404/no-record response, or on any
 * network error - callers should treat `null` as "no centralized override,
 * use existing fallback metadata". This hook never throws and never blocks
 * rendering: it always returns synchronously (cache hit or `null`) and
 * upgrades the result later via a re-render if a request is in flight.
 *
 * Only call this from static/indexable route components. Package and
 * destination detail pages already have their own SEO fields from their
 * existing APIs and must not call this hook.
 */
export const useSeoMeta = (path) => {
  const location = useLocation();
  const pathname = path || location.pathname;
  const [data, setData] = useState(() => cache.get(pathname) ?? null);

  useEffect(() => {
    let cancelled = false;
    setData(cache.get(pathname) ?? null);

    fetchSeoForPath(pathname).then((result) => {
      if (!cancelled) setData(result);
    });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  return data;
};
